/// <reference types="vite/client" />

import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import * as React from "react";
import { NoiseFilter } from "@/components/ui/noise-filter";
import { Toaster } from "@/components/ui/sonner";
import { FacebookPixel } from "@/components/FacebookPixel";
import appCss from "../styles/app.css?url";

const title = "Macrowave - Radio Broadcaster";
const description = "Turn Your Mac Into a Private Radio Station";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title
      },
      {
        name: "description",
        content: description,
      },
      {
        name: "theme-color",
        content: "#18181b",
      },
      {
        name: "msapplication-TileColor",
        content: "#18181b",
      },
      {
        name: "apple-mobile-web-app-status-bar-style",
        content: "black-translucent",
      },
      {
        property: "og:site_name",
        content: "Macrowave",
      },
      {
        property: "og:type",
        content: "website",
      },
      {
        property: "og:title",
        content: title,
      },
      {
        property: "og:description",
        content: description,
      },
      {
        property: "og:image",
        content: "/images/open-graph.jpg",
      },
      {
        property: "og:image:width",
        content: "1200",
      },
      {
        property: "og:image:height",
        content: "675",
      },
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:image",
        content: "/images/open-graph.jpg",
      },
      {
        name: "apple-itunes-app",
        content: "app-id=6746954963",
      },
    ],
    links: [
      {
        rel: "preload",
        href: "/fonts/Array-Regular.otf",
        as: "font",
        type: "font/otf",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: "/fonts/Gambarino-Regular.otf",
        as: "font",
        type: "font/otf",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "manifest",
        href: "/site.webmanifest",
      },
      {
        rel: "apple-touch-icon",
        href: "/favicon.ico",
      },
    ],
  }),
  notFoundComponent: () => {
    const StationNotFound = React.lazy(() =>
      import("../components/StationNotFound").then((m) => ({
        default: m.StationNotFound,
      }))
    );
    return (
      <React.Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-[]">
            <div className="text-zinc-400">Loading...</div>
          </div>
        }
      >
        <StationNotFound />
      </React.Suspense>
    );
  },
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <React.Suspense>
        <Outlet />
        <TanStackRouterDevtools position="bottom-left" />
      </React.Suspense>
    </RootDocument>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body style={{ backgroundColor: "#ffffff", margin: 0, padding: 0 }}>
        <FacebookPixel />
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=785646297364344&ev=PageView&noscript=1"
            alt=""
          />
        </noscript>
        <NoiseFilter />
        {children}
        <Toaster
          position="bottom-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "rgb(24 24 27)",
              border: "1px solid rgb(63 63 70)",
              color: "rgb(244 244 245)",
            },
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}
